import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {DepartmentReference} from 'app/models/group-context.model';
import {
  EMPLOYEE_STANDARD_STATUS_ACTIVE_FROM_OTHER_GROUP,
  EMPLOYEE_STANDARD_STATUS_ASSIGNED,
  EMPLOYEE_STANDARD_STATUS_KEYS,
  EMPLOYEE_STANDARD_STATUS_NOT_APPLICABLE,
  EMPLOYEE_STANDARD_STATUS_READONLY,
  EMPLOYEE_STANDARD_STATUS_READONLY_KEY,
  EMPLOYEE_STANDARD_STATUS_VALUES,
  EmployeeStandardCellResponse,
  EmployeeStandardCellResponseMap,
  EmployeeStandardCellStatus,
  EmployeeStandardCellView,
  EmployeeStandardMatrixResponse,
  EmployeeStandardMatrixUser,
  EmployeeStandardResponse,
  EmployeeStandardRowView,
  EmployeeStandardSelectableUser,
  EmployeeStandardUserResponse
} from '../../../../../../models/employee-standard-overrides.model';

const EMPLOYEE_STANDARD_CELL_KEY_SEPARATOR = '::';

@Injectable()
export class EmployeeStandardOverridesMapper {
  private departments: DepartmentReference[] = [];
  constructor(private readonly translate: TranslateService) {}

  public setDepartments(departments: DepartmentReference[]): void {
    this.departments = (departments || []).filter(department => !!department);
  }

  public restoreChanges(rows: EmployeeStandardRowView[], changes: EmployeeStandardCellView[]): EmployeeStandardRowView[] {
    return rows.map(row => Object.assign({}, row, {
      cells: row.cells.map(cell => this.restoreCellChange(cell, changes))
    }));
  }

  private restoreCellChange(cell: EmployeeStandardCellView, changes: EmployeeStandardCellView[]): EmployeeStandardCellView {
    const change = changes.find(item => item.standardId === cell.standardId && item.userId === cell.userId);
    if (!change || cell.isChecked === change.isChecked) { return cell; }
    if (cell.isDisabled) { return Object.assign({}, cell, { blockedChange: true }); }
    return Object.assign({}, cell, { isChecked: change.isChecked, isChanged: change.isChecked !== cell.originalChecked });
  }

  public mapSelectableUsers(users: EmployeeStandardUserResponse[]): EmployeeStandardSelectableUser[] {
    if (!users) {
      return [];
    }
    return users.map((user) => this.mapSelectableUser(user));
  }

  public mapMatrixUsers(users: EmployeeStandardUserResponse[]): EmployeeStandardMatrixUser[] {
    if (!users) {
      return [];
    }
    return users.map((user) => this.mapMatrixUser(user));
  }

  public createRows(
    matrix: EmployeeStandardMatrixResponse,
    users: EmployeeStandardUserResponse[]
  ): EmployeeStandardRowView[] {
    if (!matrix) {
      return [];
    }
    var cellMap = this.createCellResponseMap(matrix.cells || []);
    return (matrix.standards || []).map((standard) => this.createRow(standard, users, cellMap));
  }

  public toggleSelectableUsers(
    users: EmployeeStandardSelectableUser[],
    target: EmployeeStandardSelectableUser
  ): EmployeeStandardSelectableUser[] {
    if (!target || !target.id) {
      return users;
    }
    return users.map((user) => this.toggleSelectableUser(user, target));
  }

  public toggleRows(
    rows: EmployeeStandardRowView[],
    target: EmployeeStandardCellView
  ): EmployeeStandardRowView[] {
    if (!target || target.isDisabled) {
      return rows;
    }
    return rows.map((row) => this.toggleRowCell(row, target));
  }

  public collectChangedCells(rows: EmployeeStandardRowView[]): EmployeeStandardCellView[] {
    var changedCells: EmployeeStandardCellView[] = [];
    if (!rows) {
      return changedCells;
    }
    rows.forEach((row) => {
      row.cells.forEach((cell) => this.addChangedCell(changedCells, cell));
    });
    return changedCells;
  }

  public countSelectedUsers(users: EmployeeStandardSelectableUser[]): number {
    if (!users) {
      return 0;
    }
    return users.filter((user) => user.isSelected).length;
  }

  public countChangedCells(rows: EmployeeStandardRowView[]): number {
    if (!rows) {
      return 0;
    }
    return this.collectChangedCells(rows).length;
  }

  private mapSelectableUser(user: EmployeeStandardUserResponse): EmployeeStandardSelectableUser {
    return {
      id: user.id,
      displayName: this.resolveUserName(user),
      positionName: this.resolvePositionName(user),
      isSelected: false
    };
  }

  private mapMatrixUser(user: EmployeeStandardUserResponse): EmployeeStandardMatrixUser {
    return {
      id: user.id,
      displayName: this.resolveUserName(user),
      positionName: this.resolvePositionName(user)
    };
  }

  private createRow(
    standard: EmployeeStandardResponse,
    users: EmployeeStandardUserResponse[],
    cellMap: EmployeeStandardCellResponseMap
  ): EmployeeStandardRowView {
    return {
      id: standard.id,
      number: standard.number,
      name: this.resolveStandardName(standard),
      nameTranslation: standard.nameTranslation,
      cells: users.map((user) => this.createCell(standard, user, cellMap))
    };
  }

  private createCell(
    standard: EmployeeStandardResponse,
    user: EmployeeStandardUserResponse,
    cellMap: EmployeeStandardCellResponseMap
  ): EmployeeStandardCellView {
    const response = cellMap[this.createCellKey(standard.id, user.id)];
    const status = this.resolveCellStatus(response);
    const cell = this.createCellView(standard.id, user.id, status);
    return this.withInheritedSource(cell, response);
  }

  private withInheritedSource(cell: EmployeeStandardCellView, response: EmployeeStandardCellResponse): EmployeeStandardCellView {
    const sourceId = response && response.inheritedFromDepartmentId || null;
    const department = this.departments.find(item => item.id === sourceId);
    return Object.assign({}, cell, {
      inheritedFromDepartmentId: sourceId,
      inheritedFromDepartmentName: department ? department.name : null,
      isChecked: sourceId ? true : cell.isChecked,
      originalChecked: sourceId ? true : cell.originalChecked
    });
  }

  private createCellView(
    standardId: string,
    userId: string,
    status: EmployeeStandardCellStatus
  ): EmployeeStandardCellView {
    var isChecked = this.isCheckedStatus(status);
    return {
      id: this.createCellKey(standardId, userId),
      standardId: standardId,
      userId: userId,
      status: status,
      statusKey: this.resolveStatusKey(status),
      isChecked: isChecked,
      originalChecked: isChecked,
      isDisabled: this.isReadonlyStatus(status),
      isChanged: false,
      isActiveFromOtherGroup: status === EMPLOYEE_STANDARD_STATUS_ACTIVE_FROM_OTHER_GROUP
    };
  }

  private toggleSelectableUser(
    user: EmployeeStandardSelectableUser,
    target: EmployeeStandardSelectableUser
  ): EmployeeStandardSelectableUser {
    if (user.id !== target.id) {
      return user;
    }
    return Object.assign({}, user, { isSelected: !user.isSelected });
  }

  private toggleRowCell(
    row: EmployeeStandardRowView,
    target: EmployeeStandardCellView
  ): EmployeeStandardRowView {
    if (row.id !== target.standardId) {
      return row;
    }
    return Object.assign({}, row, { cells: this.toggleCells(row.cells, target) });
  }

  private toggleCells(
    cells: EmployeeStandardCellView[],
    target: EmployeeStandardCellView
  ): EmployeeStandardCellView[] {
    return cells.map((cell) => this.toggleMatchingCell(cell, target));
  }

  private toggleMatchingCell(
    cell: EmployeeStandardCellView,
    target: EmployeeStandardCellView
  ): EmployeeStandardCellView {
    if (cell.id !== target.id || cell.isDisabled) {
      return cell;
    }
    return this.createToggledCell(cell);
  }

  private createToggledCell(cell: EmployeeStandardCellView): EmployeeStandardCellView {
    var isChecked = !cell.isChecked;
    return Object.assign({}, cell, {
      isChecked: isChecked,
      isChanged: isChecked !== cell.originalChecked
    });
  }

  private addChangedCell(
    changedCells: EmployeeStandardCellView[],
    cell: EmployeeStandardCellView
  ): void {
    if (!cell.isChanged || cell.isDisabled) {
      return;
    }
    changedCells.push(cell);
  }

  private createCellResponseMap(cells: EmployeeStandardCellResponse[]): EmployeeStandardCellResponseMap {
    var cellMap: EmployeeStandardCellResponseMap = {};
    if (!cells) {
      return cellMap;
    }
    cells.forEach((cell) => {
      cellMap[this.createCellKey(cell.standardId, cell.userId)] = cell;
    });
    return cellMap;
  }

  private resolveCellStatus(cell: EmployeeStandardCellResponse): EmployeeStandardCellStatus {
    if (!cell || cell.inheritedFromDepartmentId || !EMPLOYEE_STANDARD_STATUS_VALUES[cell.status]) {
      return EMPLOYEE_STANDARD_STATUS_READONLY;
    }
    return EMPLOYEE_STANDARD_STATUS_VALUES[cell.status];
  }

  private resolveStatusKey(status: EmployeeStandardCellStatus): string {
    if (!EMPLOYEE_STANDARD_STATUS_KEYS[status]) {
      return EMPLOYEE_STANDARD_STATUS_READONLY_KEY;
    }
    return EMPLOYEE_STANDARD_STATUS_KEYS[status];
  }

  private isCheckedStatus(status: EmployeeStandardCellStatus): boolean {
    return status === EMPLOYEE_STANDARD_STATUS_ASSIGNED;
  }

  private isReadonlyStatus(status: EmployeeStandardCellStatus): boolean {
    return status === EMPLOYEE_STANDARD_STATUS_NOT_APPLICABLE ||
      status === EMPLOYEE_STANDARD_STATUS_READONLY;
  }

  private resolveUserName(user: EmployeeStandardUserResponse): string {
    var name = (this.resolveText(user.firstName) + ' ' + this.resolveText(user.lastName)).trim();
    if (name.length) {
      return name;
    }
    return user.id;
  }

  private resolvePositionName(user: EmployeeStandardUserResponse): string {
    if (this.hasText(user.positionName)) {
      return user.positionName.trim();
    }
    return '';
  }

  private resolveStandardName(standard: EmployeeStandardResponse): string {
    if (this.shouldUseEnglishName(standard)) {
      return standard.enName.trim();
    }
    return this.resolveDefaultStandardName(standard);
  }

  private resolveDefaultStandardName(standard: EmployeeStandardResponse): string {
    if (this.hasText(standard.ruName)) {
      return standard.ruName.trim();
    }
    return this.resolveEnglishOrNumber(standard);
  }

  private resolveEnglishOrNumber(standard: EmployeeStandardResponse): string {
    if (this.hasText(standard.enName)) {
      return standard.enName.trim();
    }
    return standard.number;
  }

  private shouldUseEnglishName(standard: EmployeeStandardResponse): boolean {
    return this.isEnglishLanguage() && this.hasText(standard.enName);
  }

  private isEnglishLanguage(): boolean {
    return this.translate.currentLang === 'en' ||
      this.translate.currentLang === 'english';
  }

  private resolveText(value: string): string {
    if (!this.hasText(value)) {
      return '';
    }
    return value.trim();
  }

  private hasText(value: string): boolean {
    return !!value && value.trim().length > 0;
  }

  private createCellKey(standardId: string, userId: string): string {
    return standardId + EMPLOYEE_STANDARD_CELL_KEY_SEPARATOR + userId;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/view-group/popups/employee-standard-overrides/services/employee-standard-overrides.mapper.ts